import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { Subscription, lastValueFrom } from 'rxjs';
import { StorageService } from 'src/app/services/auth/storage.service';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import {
  MercadoPagoAcquisitionThreeDsConfig,
  MercadoPagoRetentionThreeDsConfig,
  SetupConfigurationService
} from 'src/app/services/setup-configuration.service';

@Component({
  selector: 'app-mercadopago-retention-3ds',
  templateUrl: './mercadopago-retention-3ds.component.html',
  styleUrl: './mercadopago-retention-3ds.component.scss',
  providers: [MessageService]
})
export class MercadoPagoRetentionThreeDsComponent implements OnInit, OnDestroy {
  private title = inject(Title);
  private setupConfigurationService = inject(SetupConfigurationService);
  private messageService = inject(MessageService);
  private subscriptions = new Subscription();

  retentionConfig: MercadoPagoRetentionThreeDsConfig | null = null;
  acquisitionConfig: MercadoPagoAcquisitionThreeDsConfig | null = null;
  retentionEnabledControl = new FormControl<boolean>(false, {
    nonNullable: true
  });
  acquisitionEnabledControl = new FormControl<boolean>(false, {
    nonNullable: true
  });
  savingRetention = false;
  savingAcquisition = false;
  retentionDirty = false;
  acquisitionDirty = false;

  async ngOnInit() {
    this.title.setTitle('3DS Mercado Pago');
    this.subscriptions.add(
      this.retentionEnabledControl.valueChanges.subscribe(() =>
        this.syncRetentionDirty()
      )
    );
    this.subscriptions.add(
      this.acquisitionEnabledControl.valueChanges.subscribe(() =>
        this.syncAcquisitionDirty()
      )
    );
    await this.loadConfigs();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  async loadConfigs() {
    LoaderService.showLoader();
    try {
      const [retention, acquisition] = await Promise.all([
        lastValueFrom(
          this.setupConfigurationService.getMercadoPagoRetentionThreeDs()
        ),
        lastValueFrom(
          this.setupConfigurationService.getMercadoPagoAcquisitionThreeDs()
        )
      ]);
      this.retentionConfig = retention;
      this.acquisitionConfig = acquisition;
      this.retentionEnabledControl.setValue(!!retention?.isEnabled, {
        emitEvent: false
      });
      this.acquisitionEnabledControl.setValue(!!acquisition?.isEnabled, {
        emitEvent: false
      });
      this.syncRetentionDirty();
      this.syncAcquisitionDirty();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async saveRetention() {
    if (!this.retentionConfig || this.savingRetention || !this.retentionDirty) {
      return;
    }
    const enabled = this.retentionEnabledControl.value;
    this.savingRetention = true;
    LoaderService.showLoader();
    try {
      const updated = await lastValueFrom(
        this.setupConfigurationService.updateMercadoPagoRetentionThreeDs(
          enabled,
          StorageService.userId != null
            ? `user-${StorageService.userId}`
            : 'admin'
        )
      );
      this.retentionConfig = updated;
      this.retentionEnabledControl.setValue(!!updated.isEnabled, {
        emitEvent: false
      });
      this.syncRetentionDirty();
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: `3DS no Pagar agora ${enabled ? 'habilitado' : 'desabilitado'}.`
      });
    } catch (error) {
      this.retentionEnabledControl.setValue(!!this.retentionConfig.isEnabled, {
        emitEvent: false
      });
      this.syncRetentionDirty();
      AppDialogService.showErrorDialog(error);
    } finally {
      this.savingRetention = false;
      LoaderService.showLoader(false);
    }
  }

  async saveAcquisition() {
    if (
      !this.acquisitionConfig ||
      this.savingAcquisition ||
      !this.acquisitionDirty
    ) {
      return;
    }
    const enabled = this.acquisitionEnabledControl.value;
    this.savingAcquisition = true;
    LoaderService.showLoader();
    try {
      const updated = await lastValueFrom(
        this.setupConfigurationService.updateMercadoPagoAcquisitionThreeDs(
          enabled,
          StorageService.userId != null
            ? `user-${StorageService.userId}`
            : 'admin'
        )
      );
      this.acquisitionConfig = updated;
      this.acquisitionEnabledControl.setValue(!!updated.isEnabled, {
        emitEvent: false
      });
      this.syncAcquisitionDirty();
      this.messageService.add({
        severity: 'success',
        summary: 'Sucesso',
        detail: `3DS nas novas assinaturas ${
          enabled ? 'habilitado' : 'desabilitado'
        }.`
      });
    } catch (error) {
      this.acquisitionEnabledControl.setValue(
        !!this.acquisitionConfig.isEnabled,
        { emitEvent: false }
      );
      this.syncAcquisitionDirty();
      AppDialogService.showErrorDialog(error);
    } finally {
      this.savingAcquisition = false;
      LoaderService.showLoader(false);
    }
  }

  private syncRetentionDirty() {
    this.retentionDirty =
      !!this.retentionConfig &&
      this.retentionEnabledControl.value !== !!this.retentionConfig.isEnabled;
  }

  private syncAcquisitionDirty() {
    this.acquisitionDirty =
      !!this.acquisitionConfig &&
      this.acquisitionEnabledControl.value !==
        !!this.acquisitionConfig.isEnabled;
  }
}
